import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Users, Ban, MapPin, SlidersHorizontal } from "lucide-react";
import { ChipInput, Field, INPUT_CLS } from "./ChipInput";
import { SENIORITY, sizeLabel } from "./onboardingModel";

const Q = ({ n, icon: Icon, title, hint, children, testId }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-3" data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className="h-7 w-7 rounded-full bg-ism-teal/15 text-ism-teal text-xs flex items-center justify-center font-medium">{n}</span>
      <Icon className="h-4 w-4 text-ism-teal" />
      <h3 className="text-white">{title}</h3>
    </div>
    <p className="text-xs text-gray-500">{hint}</p>
    {children}
  </div>
);

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export const StepTargeting = ({ data, patch, patchSpec, busy, onBack, onNext }) => {
  const spec = data.spec || {};
  const entity = (spec.entityTypes || [])[0] || "company";
  const size = sizeLabel(entity);
  const sz = spec.size || {};
  const setSize = (k, v) => patchSpec({ size: { ...sz, [k]: v === "" ? null : Number(v) } });

  return (
    <div className="space-y-5" data-testid="onboarding-step-targeting">
      <div>
        <h2 className="text-2xl text-white font-light">Three things only you know</h2>
        <p className="text-gray-400 mt-1 text-sm">Everything else I inferred. These three sharpen the hunt more than anything.</p>
      </div>

      <Q n={1} icon={Users} title="Your best customers" hint="Name 2–5 customers you'd clone. I find look-alikes of these first." testId="targeting-seeds">
        <ChipInput values={data.bestCustomers} onChange={(v) => { patch({ bestCustomers: v }); patchSpec({ lookalikeSeeds: v }); }} placeholder="e.g. Rockefeller Capital, Bessemer Trust" testId="targeting-customers" />
      </Q>

      <Q n={2} icon={Ban} title="Who should I never target?" hint="Competitors, existing customers, vendors, recruiters — anything that wastes a touch." testId="targeting-negatives">
        <ChipInput values={spec.negatives || []} onChange={(v) => patchSpec({ negatives: v })} placeholder="e.g. Addepar, recruiters, wealth-tech vendors" testId="targeting-negatives-input" accent="red" />
      </Q>

      <Q n={3} icon={MapPin} title="Where do you sell, and how big?" hint="Geography and size are hard filters — prospects outside them are dropped." testId="targeting-geo-size">
        <ChipInput values={spec.geos || []} onChange={(v) => patchSpec({ geos: v })} placeholder="e.g. United States, Texas, United Kingdom" testId="targeting-geos" />
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Min ${size.key}${size.money ? " ($)" : ""}`}>
            <input type="number" value={sz[size.min] ?? ""} onChange={(e) => setSize(size.min, e.target.value)} className={INPUT_CLS} placeholder={size.money ? "100000000" : "10"} data-testid="targeting-size-min" />
          </Field>
          <Field label={`Max ${size.key}${size.money ? " ($)" : ""}`}>
            <input type="number" value={sz[size.max] ?? ""} onChange={(e) => setSize(size.max, e.target.value)} className={INPUT_CLS} placeholder={size.money ? "5000000000" : "500"} data-testid="targeting-size-max" />
          </Field>
        </div>
      </Q>

      <details className="rounded-xl border border-white/10 bg-white/[0.02] p-5 group" data-testid="targeting-sharpen">
        <summary className="cursor-pointer list-none flex items-center gap-2 text-sm text-gray-300 hover:text-white">
          <SlidersHorizontal className="h-4 w-4 text-ism-teal" /> Sharpen (optional): seniority &amp; buying signals
        </summary>
        <div className="mt-4 space-y-4">
          <Field label="Buyer seniority">
            <div className="flex flex-wrap gap-2">
              {SENIORITY.map((s) => {
                const on = (spec.seniority || []).includes(s);
                return (
                  <button key={s} onClick={() => patchSpec({ seniority: toggle(spec.seniority || [], s) })}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-ism-teal/20 border-ism-teal/40 text-ism-teal" : "border-white/10 text-gray-400 hover:border-white/20"}`}
                    data-testid={`seniority-${s}`}>
                    {s.replace("_", "-")}
                  </button>
                );
              })}
            </div>
          </Field>
          <ChipInput label="Buying signals" hint="Triggers that mean 'now is the time' — raised a round, new CRO, new filing, opened a location." values={spec.signals || []} onChange={(v) => patchSpec({ signals: v })} placeholder="Add a signal" testId="targeting-signals" />
        </div>
      </details>

      <div className="flex justify-between pt-2">
        <Button variant="ghost" onClick={onBack} className="text-gray-400 hover:text-white" data-testid="onboarding-back-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        <Button onClick={onNext} disabled={busy} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium rounded-xl px-6" data-testid="onboarding-targeting-next">Show me 3 prospects <ArrowRight className="h-4 w-4 ml-2" /></Button>
      </div>
    </div>
  );
};
